import { useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { motion } from 'framer-motion'
import { RotateCcw, Sparkles, Calculator, AlertTriangle, ClipboardCheck } from 'lucide-react'
import SubpageHero from '@/components/common/SubpageHero'
import PageContainer from '@/components/layout/PageContainer'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import SEO from '@/components/common/SEO'
import { ROUTES } from '@/lib/constants'
import { useSelfAssessmentStore } from '@/stores/selfAssessmentStore'
import { getEstimatedGrade, getCategoryScores } from '@/data/selfAssessmentQuestions'
import { trackCompleteSelfAssessment } from '@/lib/analytics'

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
}

const NEXT_STEPS = [
  { step: 1, title: '국민건강보험공단에 등급 신청', description: '가까운 공단 지사 방문 또는 1577-1000 전화' },
  { step: 2, title: '공단 직원 방문조사', description: '52개 항목으로 심신 상태를 정밀 조사합니다' },
  { step: 3, title: '등급판정위원회 심의', description: '방문조사 결과와 의사 소견서를 종합 심의합니다' },
  { step: 4, title: '등급 판정 결과 통보', description: '신청일로부터 약 30일 이내 결과를 받습니다' },
  { step: 5, title: '장기요양 서비스 이용', description: '등급에 맞는 시설 또는 재가급여를 이용합니다' },
]

export default function SelfAssessmentResultPage() {
  const navigate = useNavigate()
  const { answers, reset } = useSelfAssessmentStore()

  const totalScore = Object.values(answers).reduce((sum, s) => sum + s, 0)
  const result = getEstimatedGrade(totalScore)
  const categoryScores = getCategoryScores(answers)
  const hasAnswers = Object.keys(answers).length > 0

  useEffect(() => {
    if (hasAnswers) trackCompleteSelfAssessment(result.grade, totalScore)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleRetry = () => {
    reset()
    navigate('/guide/self-assessment')
  }

  if (!hasAnswers) {
    return (
      <>
        <SubpageHero title="자가진단 결과" description="예상 장기요양등급 분석" icon={<ClipboardCheck size={20} className="text-white" />} />
        <PageContainer>
          <div className="flex flex-col items-center justify-center gap-4 py-20 text-center">
            <p className="text-body text-cb-gray-500">진단 결과가 없습니다.</p>
            <Button variant="primary" onClick={() => navigate('/guide/self-assessment')}>
              자가진단 시작하기
            </Button>
          </div>
        </PageContainer>
      </>
    )
  }

  return (
    <>
      <SEO title="자가진단 결과" url="/guide/self-assessment/result" />
      <SubpageHero title="자가진단 결과" description="예상 장기요양등급 분석" icon={<ClipboardCheck size={20} className="text-white" />} />
      <PageContainer>
        <motion.div
          className="flex flex-col gap-6"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.06 } } }}
        >
          {/* 결과 카드 */}
          <motion.section variants={fadeUp}>
            <div
              className="rounded-2xl p-6 text-white text-center"
              style={{ background: `linear-gradient(135deg, ${result.color}dd, ${result.color}88)` }}
            >
              <p className="text-sm text-white/80 mb-2">예상 장기요양등급</p>
              <h1 className="text-4xl font-bold mb-1">{result.grade}</h1>
              <p className="text-base font-medium text-white/90">{result.label}</p>
              <div className="mt-4 inline-block rounded-full bg-white/20 px-4 py-1.5 text-sm font-semibold">
                총점: {totalScore} / 24점
              </div>
            </div>
          </motion.section>

          {/* 등급 설명 */}
          <motion.section variants={fadeUp}>
            <Card>
              <p className="text-body-sm text-cb-gray-700 leading-relaxed">{result.description}</p>
            </Card>
          </motion.section>

          {/* 카테고리별 점수 */}
          <motion.section variants={fadeUp}>
            <Card>
              <h2 className="text-body font-bold text-cb-gray-900 mb-4">영역별 점수</h2>
              <div className="flex flex-col gap-3">
                {categoryScores.map((cat) => {
                  const pct = cat.maxScore > 0 ? (cat.score / cat.maxScore) * 100 : 0
                  return (
                    <div key={cat.category}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-caption font-medium text-cb-gray-700">{cat.category}</span>
                        <span className="text-caption text-cb-gray-500">{cat.score}/{cat.maxScore}</span>
                      </div>
                      <div className="h-2.5 rounded-full bg-cb-gray-100 overflow-hidden">
                        <motion.div
                          className="h-full rounded-full"
                          style={{ background: result.color }}
                          initial={{ width: 0 }}
                          animate={{ width: `${pct}%` }}
                          transition={{ duration: 0.6, delay: 0.2 }}
                        />
                      </div>
                    </div>
                  )
                })}
              </div>
            </Card>
          </motion.section>

          {/* 면책 고지 */}
          <motion.section variants={fadeUp}>
            <div className="flex gap-3 rounded-xl bg-amber-50 p-4">
              <AlertTriangle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-caption text-amber-800 leading-relaxed">
                본 자가진단은 간이 참고용이며, 실제 장기요양등급 판정과 다를 수 있습니다.
                정확한 등급 판정은 <strong>국민건강보험공단(1577-1000)</strong>의 방문조사를 통해 이루어집니다.
              </p>
            </div>
          </motion.section>

          {/* 다음 단계 안내 */}
          <motion.section variants={fadeUp}>
            <Card>
              <h2 className="text-body font-bold text-cb-gray-900 mb-4">등급 신청 절차</h2>
              <div className="flex flex-col gap-3">
                {NEXT_STEPS.map((item) => (
                  <div key={item.step} className="flex gap-3">
                    <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-cb-primary/10 text-xs font-bold text-cb-primary">
                      {item.step}
                    </div>
                    <div>
                      <p className="text-body-sm font-semibold text-cb-gray-800">{item.title}</p>
                      <p className="text-caption text-cb-gray-500">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </motion.section>

          {/* CTA */}
          <motion.section variants={fadeUp} className="flex flex-col gap-3 pb-4">
            <Button variant="primary" size="lg" fullWidth onClick={() => navigate(ROUTES.RECOMMEND)}>
              <Sparkles size={16} /> 맞춤 시설 추천받기
            </Button>
            <Button variant="secondary" size="lg" fullWidth onClick={() => navigate(ROUTES.COST_SIMULATOR)}>
              <Calculator size={16} /> 등급별 비용 계산하기
            </Button>
            <Button variant="ghost" size="lg" fullWidth onClick={handleRetry}>
              <RotateCcw size={16} /> 다시 진단하기
            </Button>
          </motion.section>
        </motion.div>
      </PageContainer>
    </>
  )
}
